import React, { useState, useRef, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Sparkles, Loader2, Rocket, Globe, Smartphone, Bot, Check, ArrowLeft, Copy, ExternalLink,
  Save, FolderOpen, Trash2, Link2, Send,
} from "lucide-react";
import { toast } from "sonner";
import { api, local } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";

const MODES = [
  { id: "website", label: "بناء موقع", Icon: Globe, hint: "متجر، صفحة هبوط، موقع شركة…", preview: true },
  { id: "app", label: "بناء تطبيق", Icon: Smartphone, hint: "تطبيق ويب متكامل بلوحة تحكم…", preview: true },
  { id: "agent", label: "بناء وكيل ذكاء", Icon: Bot, hint: "وكيل محادثة، مساعد مبيعات، بوت…", preview: false },
];

const BUILD_STEPS = [
  "نفهم فكرتك…", "نخطط البنية…", "نجهّز الواجهة…", "ننشئ الخادم والمنطق…",
  "نصمم قاعدة البيانات…", "نوصل التكاملات…", "نطبّق نظام AQL4…", "نجري المعاينة النهائية…",
];

const PROVIDERS = [
  { id: "openai", label: "GPT" },
  { id: "anthropic", label: "Claude" },
  { id: "gemini", label: "Gemini" },
];

export default function Build() {
  const [mode, setMode] = useState("website");
  const [provider, setProvider] = useState("openai");
  const [prompt, setPrompt] = useState("");
  const [step, setStep] = useState(-1);
  const [building, setBuilding] = useState(false);
  const [plan, setPlan] = useState("");
  const [html, setHtml] = useState("");
  const [pubUrl, setPubUrl] = useState("");
  const [aqlIdentity, setAqlIdentity] = useState(null);
  const [agentName, setAgentName] = useState("");
  const [projectId, setProjectId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [searchParams] = useSearchParams();
  const [showProjects, setShowProjects] = useState(false);
  const [domainForm, setDomainForm] = useState({ open: false, domain: "", email: local.getEmail() || "", note: "" });
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const loadProjects = () => api.listProjects().then(setProjects);
  useEffect(() => { loadProjects(); }, []);
  useEffect(() => {
    const templatePrompt = searchParams.get("prompt");
    if (templatePrompt) {
      setMode("website");
      setPrompt(templatePrompt);
    }
  }, [searchParams]);

  const cur = MODES.find((m) => m.id === mode);

  const saveProject = async () => {
    if (!prompt.trim()) { toast.error("صِف مشروعك أولًا"); return; }
    try {
      const p = await api.saveProject({ id: projectId, name: prompt.slice(0, 50) || "مشروع", type: mode, prompt, plan, html, owner_email: local.getEmail() });
      setProjectId(p.id); loadProjects(); toast.success("تم حفظ المشروع في قاعدتك");
    } catch { toast.error("تعذّر الحفظ"); }
  };
  const openProject = (p) => {
    setMode(p.type); setPrompt(p.prompt || ""); setPlan(p.plan || ""); setHtml(p.html || "");
    setAqlIdentity(p.aql_code ? { aql_code: p.aql_code, subdomain: p.subdomain, domain_status: p.domain_status || "needs_dns" } : null);
    setProjectId(p.id); setPubUrl(p.published_url || ""); setShowProjects(false); setStep(-1);
  };
  const projectParam = searchParams.get("projectId");
  useEffect(() => {
    if (projectParam) api.getProject(projectParam).then((p) => {
      setMode(p.type); setPrompt(p.prompt || ""); setPlan(p.plan || ""); setHtml(p.html || "");
      setAqlIdentity(p.aql_code ? { aql_code: p.aql_code, subdomain: p.subdomain, domain_status: p.domain_status || "needs_dns" } : null);
      setProjectId(p.id); setPubUrl(p.published_url || ""); setShowProjects(false); setStep(-1);
    }).catch(() => toast.error("تعذّر فتح المشروع"));
  }, [projectParam]);
  const removeProject = async (id) => { await api.deleteProject(id); loadProjects(); if (id === projectId) setProjectId(null); };

  const submitDomainRequest = async () => {
    if (!domainForm.domain.trim()) { toast.error("أدخل النطاق المطلوب"); return; }
    try {
      await api.createDomainRequest({ project_id: projectId || "", domain: domainForm.domain.trim(), requester_email: domainForm.email, note: domainForm.note });
      toast.success("تم إرسال طلب النطاق للإدارة للموافقة");
      setDomainForm({ open: false, domain: "", email: domainForm.email, note: "" });
    } catch { toast.error("تعذّر إرسال الطلب"); }
  };

  const publishSite = async () => {
    try {
      const r = await api.createSite({ title: prompt.slice(0, 50) || "موقع AQL4", html, prompt, kind: mode });
      setPubUrl(r.url); setAqlIdentity(r); toast.success("تم النشر؛ النطاق الفرعي ينتظر DNS");
      const p = await api.saveProject({ id: projectId, name: prompt.slice(0, 50) || "موقع", type: mode, prompt, plan, html, published: true, published_url: r.url, aql_code: r.aql_code, subdomain: r.subdomain, owner_email: local.getEmail() });
      setProjectId(p.id); loadProjects();
    }
    catch (e) { toast.error("تعذّر النشر"); }
  };
  const publishAgent = async () => {
    if (!agentName.trim()) { toast.error("أدخل اسم الوكيل"); return; }
    try {
      const r = await api.createAgent({ name: agentName, system: prompt, greeting: "مرحبًا! كيف أساعدك اليوم؟", provider });
      setPubUrl(r.url); setAqlIdentity(r); toast.success("تم نشر الوكيل؛ النطاق الفرعي ينتظر DNS");
      const p = await api.saveProject({ id: projectId, name: agentName, type: "agent", prompt, plan, published: true, published_url: r.url, aql_code: r.aql_code, subdomain: r.subdomain, owner_email: local.getEmail() });
      setProjectId(p.id); loadProjects();
    }
    catch (e) { toast.error("تعذّر النشر"); }
  };

  const build = async () => {
    if (!prompt.trim()) { toast.error("صِف ما تريد بناءه"); return; }
    setBuilding(true); setPlan(""); setHtml(""); setStep(0);
    timers.current.forEach(clearTimeout); timers.current = [];
    BUILD_STEPS.forEach((_, i) => timers.current.push(setTimeout(() => setStep(i), i * 700)));
    const label = mode === "agent" ? "وكيل ذكاء اصطناعي" : mode === "app" ? "تطبيق ويب متكامل" : "موقع ويب";
    try {
      const tasks = [api.aiBuilder({ prompt: `${label}: ${prompt}`, provider })];
      if (cur.preview) tasks.push(api.aiPage({ prompt: `${label}: ${prompt}` }));
      const res = await Promise.all(tasks);
      setPlan(res[0].content);
      if (cur.preview && res[1]) setHtml(res[1].html);
      toast.success("اكتمل البناء!");
    } catch (e) { toast.error("تعذّر البناء — تأكد من رصيد الذكاء الاصطناعي"); }
    setBuilding(false); setStep(BUILD_STEPS.length);
  };

  return (
    <main className="relative min-h-screen pt-28">
      <div className="absolute inset-0 bg-grid opacity-25" />
      <div className="absolute left-1/2 top-20 h-72 w-[720px] -translate-x-1/2 hero-glow blur-3xl" />
      <section className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white"><ArrowLeft className="h-4 w-4 rotate-180" /> الرئيسية</Link>
        <div className="mt-4 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-cyan/25 bg-cyan/5 px-4 py-1.5 text-xs font-medium text-cyan">
            <Rocket className="h-3.5 w-3.5" /> استوديو البناء الكامل
          </span>
          <h1 className="mt-4 font-display text-5xl font-extrabold text-white md:text-6xl">
            ابنِ <span className="gradient-text">أي شيء</span> بالذكاء
          </h1>
          <p className="mt-3 text-muted-foreground">مواقع · تطبيقات · وكلاء ذكاء — صِف فكرتك ونبنيها لك كاملة.</p>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {MODES.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => { setMode(id); setPlan(""); setHtml(""); setStep(-1); }}
              className={`flex items-center gap-2 rounded-xl border px-5 py-3 text-sm font-semibold transition-colors ${mode === id ? "border-cyan/50 bg-cyan/10 text-cyan" : "border-white/10 text-muted-foreground hover:text-white"}`}>
              <Icon className="h-5 w-5" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <Button data-testid="save-project-btn" onClick={saveProject} variant="outline" className="gap-2 rounded-lg border-white/15 bg-transparent text-white hover:bg-white/5"><Save className="h-4 w-4" /> {projectId ? "حفظ التعديلات" : "احفظ كمشروع"}</Button>
          <Button data-testid="my-projects-btn" onClick={() => setShowProjects((v) => !v)} variant="outline" className="gap-2 rounded-lg border-white/15 bg-transparent text-white hover:bg-white/5"><FolderOpen className="h-4 w-4" /> مشاريعي ({projects.length})</Button>
        </div>
        {showProjects && (
          <div className="mx-auto mt-3 max-w-2xl space-y-2 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            {projects.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                <button onClick={() => openProject(p)} className="flex-1 truncate text-right text-sm text-white hover:text-cyan">{p.name} <span className="text-xs text-muted-foreground">· {p.type}{p.published ? " · منشور" : ""}</span></button>
                <button onClick={() => removeProject(p.id)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {!projects.length && <p className="py-3 text-center text-xs text-muted-foreground">لا توجد مشاريع محفوظة بعد.</p>}
          </div>
        )}

        <div className="mt-8 grid gap-5 lg:grid-cols-2">
          <div className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <div className="mb-3 flex gap-1.5">
              {PROVIDERS.map((p) => (<button key={p.id} onClick={() => setProvider(p.id)} className={`rounded-md px-2.5 py-1 text-xs font-medium ${provider === p.id ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}>{p.label}</button>))}
            </div>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={6}
              placeholder={cur.hint} className="resize-none border-white/10 bg-white/5 text-right text-white" />
            <Button onClick={build} disabled={building} className="btn-cyan mt-3 h-12 gap-2 rounded-xl text-base font-semibold hover:bg-transparent">
              {building ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />} ابنِ الآن
            </Button>
            {step >= 0 && (
              <div className="mt-4 space-y-2 rounded-xl border border-white/10 bg-black/20 p-4">
                {BUILD_STEPS.map((s, i) => i <= step && (
                  <div key={s} className="flex items-center gap-2 text-sm">
                    {i < step ? <Check className="h-4 w-4 text-emerald-400" strokeWidth={3} /> : <Loader2 className="h-4 w-4 animate-spin text-cyan" />}
                    <span className={i < step ? "text-muted-foreground" : "text-white"}>{s}</span>
                  </div>
                ))}
              </div>
            )}
            {plan && (
              <div className="mt-4">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-semibold text-white">خطة البناء</span>
                  <button onClick={() => { navigator.clipboard?.writeText(plan); toast.success("تم النسخ"); }}><Copy className="h-4 w-4 text-muted-foreground hover:text-white" /></button>
                </div>
                <div className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg bg-black/20 p-4 text-sm leading-relaxed text-cyan-50">{plan}</div>
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0a100d]">
            <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
              <div className="flex gap-1.5"><span className="h-3 w-3 rounded-full bg-rose-400/70" /><span className="h-3 w-3 rounded-full bg-amber-400/70" /><span className="h-3 w-3 rounded-full bg-emerald-400/70" /></div>
              <div className="mx-auto flex items-center gap-2 rounded-lg bg-white/5 px-3 py-1 font-en text-xs text-muted-foreground" dir="ltr"><Rocket className="h-3.5 w-3.5" /> aql4.app/preview</div>
            </div>
            {building && <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-cyan" /></div>}
            {!building && html && <iframe title="preview" srcDoc={html} className="h-[62vh] w-full bg-white" />}
            {!building && !html && (
              <div className="flex h-[60vh] flex-col items-center justify-center text-center text-muted-foreground">
                {mode === "agent" ? <Bot className="h-12 w-12 opacity-30" /> : <Rocket className="h-12 w-12 opacity-30" />}
                <p className="mt-3 text-sm">{mode === "agent" ? "ستظهر مواصفات الوكيل في لوحة الخطة" : "ستظهر معاينة مباشرة هنا بعد البناء"}</p>
              </div>
            )}
          </div>
        </div>

        {(html || plan) && (
          <div className="mt-6 rounded-2xl border border-cyan/20 bg-cyan/5 p-5">
            <h3 className="font-display font-bold text-white">النشر الحقيقي</h3>
            <p className="mt-1 text-sm text-muted-foreground">يُحفظ في قاعدة بيانات حقيقية ويعطيك رابطًا يعمل فعلاً.</p>
            {mode !== "agent" ? (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button onClick={publishSite} disabled={!html} className="btn-cyan gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><Rocket className="h-4 w-4" /> نشر الموقع</Button>
                {pubUrl && <a href={pubUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-en text-sm text-cyan" dir="ltr"><ExternalLink className="h-4 w-4" /> {window.location.origin}{pubUrl}</a>}
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input value={agentName} onChange={(e) => setAgentName(e.target.value)} placeholder="اسم الوكيل" className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-right text-sm text-white" />
                <Button onClick={publishAgent} className="btn-cyan gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><Bot className="h-4 w-4" /> نشر الوكيل</Button>
                {pubUrl && <a href={pubUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-en text-sm text-cyan" dir="ltr"><ExternalLink className="h-4 w-4" /> {window.location.origin}{pubUrl}</a>}
              </div>
            )}
            {pubUrl && <button onClick={() => { navigator.clipboard?.writeText(window.location.origin + pubUrl); toast.success("تم نسخ الرابط"); }} className="mt-2 text-xs text-muted-foreground hover:text-white">نسخ الرابط</button>}
            {aqlIdentity?.aql_code && <div className="mt-3 rounded-lg border border-gold/20 bg-gold/5 p-3"><p className="text-xs font-semibold text-gold">رمز النشر AQL</p><p className="mt-1 font-en text-sm text-white" dir="ltr">{aqlIdentity.aql_code}</p><p className="mt-1 font-en text-xs text-cyan" dir="ltr">{aqlIdentity.subdomain}</p><p className="mt-1 text-[11px] text-muted-foreground">حالة النطاق: {aqlIdentity.domain_status === "active" ? "فعال" : "بانتظار مزامنة DNS وإعداد المضيف"}</p></div>}

            <div className="mt-4 border-t border-white/10 pt-4">
              <button data-testid="request-domain-toggle" onClick={() => setDomainForm((d) => ({ ...d, open: !d.open }))} className="flex items-center gap-2 text-sm font-semibold text-gold hover:text-amber-300">
                <Link2 className="h-4 w-4" /> اطلب نطاقًا خاصًا لهذا المشروع
              </button>
              {domainForm.open && (
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <Input data-testid="req-domain-input" value={domainForm.domain} onChange={(e) => setDomainForm({ ...domainForm, domain: e.target.value })} placeholder="mysite.com" dir="ltr" className="border-white/10 bg-white/5 text-white" />
                  <Input value={domainForm.email} onChange={(e) => setDomainForm({ ...domainForm, email: e.target.value })} placeholder="بريدك" dir="ltr" className="border-white/10 bg-white/5 text-white" />
                  <Button data-testid="req-domain-submit" onClick={submitDomainRequest} className="btn-gold gap-2 rounded-lg font-semibold hover:bg-transparent"><Send className="h-4 w-4" /> إرسال الطلب</Button>
                  <p className="text-xs text-muted-foreground sm:col-span-3">يراجع الطلبَ فريق الإدارة ويربط النطاق بعد الموافقة. (ربط DNS يتم على مستوى المنصة).</p>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
      <div className="h-20" />
    </main>
  );
}
